import { useState } from "react";
import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  ReferenceInput,
  Toolbar,
  SaveButton,
  Loading,
  useGetIdentity,
  useNotify,
  useRedirect,
} from "react-admin";
import {
  Typography,
  CardContent,
  Card,
  Box,
  Divider,
  Alert,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import ComponentEdit from "../../components/PageComponents/ComponentEdit";
import FormField from "../../components/Common/FormField";
import { statusText } from "../../components/Label";

export   const listPermissions = () => {
  return [
    { id: 1, name: "Reader" },
    { id: 4, name: "Operator" },
    { id: 16, name: "Account Admin" },
    { id: 32, name: "Reseller" },
    { id: 64, name: "Coinhouse Operator" },
    { id: 128, name: "Coinhouse Admin" },
    { id: 2048, name: "Coinhouse Super Admin" },
  ];
};
const UserCreate = () => {
  const { t }: { t: any } = useTranslation();
  const [showAlert, setShowAlert] = useState(false);
  const { data: identity, isLoading } = useGetIdentity();
  const notify = useNotify();
  const redirect = useRedirect();

  // A user can only grant a role up to their own level; nothing is offered until the identity is known.
  const getAvailablePermissions = () => {
    const ownLevel = Number(identity?.permissionLevel) || 0;
    return listPermissions()
      .filter((permission) => permission.id <= ownLevel)
      .map((permission) => ({ ...permission, name: t(statusText(permission.name)) }));
  };

  const onSuccess = (data: any) => {
    notify(t("User created, an invitation email has been sent"));
    redirect('list', 'users');
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <ComponentEdit
      title={t("New user")}
      subtitle={t("Invite someone to the admin and give them a role on an account.")}
    >
      <Card>
        <Box
          p={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6">
              {t("User details")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("The user receives an email to set their password.")}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <CardContent
          sx={{
            p: 4,
          }}
        >
          <Create redirect="list" mutationOptions={{ onSuccess }}>
            <SimpleForm toolbar={<Toolbar sx={{ m: 2 }}><SaveButton label={t("Create user")} /></Toolbar>}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Account")}>
                    <ReferenceInput source="accountId" reference="accounts">
                      <SelectInput label={t("Account")} fullWidth onChange={() => setShowAlert(true)} />
                    </ReferenceInput>
                  </FormField>
                  {showAlert && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {t("The account can't be changed after creation.")}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Name")}>
                    <TextInput source="name" label={t("Name")} fullWidth />
                  </FormField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Email")}>
                    <TextInput source="email" label={t("Email")} type="email" fullWidth />
                  </FormField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Role")}>
                    <SelectInput
                      source="permissionLevel"
                      label={t("Role")}
                      fullWidth
                      choices={getAvailablePermissions()}
                    />
                  </FormField>
                </Grid>
              </Grid>
            </SimpleForm>
          </Create>
        </CardContent>
      </Card>
    </ComponentEdit>
  );
};

export default UserCreate;
